import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface LoginModalProps {
  onClose: () => void;
}

export default function LoginModal({ onClose }: LoginModalProps) {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: signInError, isAdmin } = await signIn(username, password);

    if (signInError) {
      setError(signInError);
    } else {
      onClose();
      if (isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/member', { replace: true });
      }
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal - Modern Design */}
      <div className="relative bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-fade-in-up">
        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 via-amber-500 to-rose-400"></div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 cursor-pointer z-10"
        >
          <i className="ri-close-line text-lg text-gray-500"></i>
        </button>

        <div className="p-8 pt-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-amber-500/20">
              <i className="ri-user-line text-2xl text-white"></i>
            </div>
            <h2 className="text-2xl font-black text-gray-900">ورود به حساب کاربری</h2>
            <p className="text-gray-400 text-sm mt-2">به کارلند خوش آمدید</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 p-3.5 rounded-2xl bg-red-50 border border-red-100 text-right animate-fade-in">
              <p className="text-red-600 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line text-base"></i>
                {error}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 text-right">نام کاربری</label>
              <div className="relative group">
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <i className="ri-user-3-line text-gray-400 group-focus-within:text-amber-500 transition-colors"></i>
                </div>
                <input
                  type="text"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="نام کاربری خود را وارد کنید"
                  className="w-full pr-11 pl-4 py-3.5 rounded-2xl bg-gray-50 border border-gray-100 text-sm text-right focus:outline-none focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-50 transition-all duration-200"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 text-right">رمز عبور</label>
              <div className="relative group">
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <i className="ri-lock-2-line text-gray-400 group-focus-within:text-amber-500 transition-colors"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="رمز عبور خود را وارد کنید"
                  className="w-full pr-11 pl-12 py-3.5 rounded-2xl bg-gray-50 border border-gray-100 text-sm text-right focus:outline-none focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-50 transition-all duration-200"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors duration-200 cursor-pointer"
                >
                  <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line text-lg`}></i>
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 mt-2 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold text-sm hover:from-amber-600 hover:to-amber-700 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 active:scale-[0.98]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  در حال ورود...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-login-box-line text-lg"></i>
                  ورود به حساب
                </span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}